import isEqual from "lodash.isequal"

export type AddressBookContract = {
  address: string
  name: string
  description?: string
}

const ADDRESS_BOOK: Record<string, Record<string, AddressBookContract[]>> = {
  "https://api.tally.xyz": {
    // Compound /gov/compound
    "eip155:1:0x309a862bbC1A00e45506cB8A802D1ff10004c8C0": [
      {
        name: "Configurator",
        address: "0x316f9708bB98af7dA9c68C1C3b5e79039cD336E3",
      },
      {
        name: "CometProxyAdmin",
        address: "0x1EC63B5883C3481134FD50D5DAebc83Ecd2E8779",
      },
      {
        name: "CometFactory",
        address: "0x1fA408992e74A42D1787E28b880C451452E8C958",
      },
      {
        name: "Rewards",
        address: "0x1B0e765F6224C21223AeA2af16c1C46E38885a40",
      },
      {
        name: "ENS Public Resolver",
        address: "0x4976fb03C32e5B8cfe2b6cCB31c09Ba78EBaBa41",
      },
      {
        name: "Comets > USDC",
        address: "0xc3d688B66703497DAA19211EEdff47f25384cdc3",
      },
      {
        name: "Comets > WETH",
        address: "0xA17581A9E3356d9A858b789D68B4d866e593aE94",
      },
      {
        name: "Comets > USDT",
        address: "0x3Afdc9BCA9213A35503b077a6072F3D0d5AB0840",
      },
      {
        name: "Comets > wstETH",
        address: "0x3D0bb1ccaB520A66e607822fC55BC921738fAFE3",
      },
      {
        name: "Comets > USDS",
        address: "0x5D409e56D886231aDAf00c8775665AD0f9897b56",
      },
      {
        name: "Comets > WBTC",
        address: "0xe85Dc543813B8c2CFEaAc371517b925a166a9293",
      },
    ],
  },

  "https://api.staging.tally.xyz": {
    // /gov/compound
    "eip155:1:0x309a862bbC1A00e45506cB8A802D1ff10004c8C0": [
      {
        name: "Governor",
        description:
          "Governor Bravo is the governance module of the protocol; it allows addresses with more than 25,000 COMP to propose changes to the protocol.",
        address: "0xc0Da02939E1441F497fd74F78cE7Decb17B66529",
      },
      {
        name: "cUSDCv3",
        description:
          "This is the main proxy contract for interacting with the first Compound III market. The address is fixed and independent from future upgrades to the market.",
        address: "0xc3d688B66703497DAA19211EEdff47f25384cdc3",
      },
      {
        name: "Comet Factory",
        description:
          "This is the factory contract capable of producing instances of the Comet implementation/logic contract, and invoked by the Configurator.",
        address: "0xa7F7De6cCad4D83d81676717053883337aC2c1b4",
      },
    ],

    // /gov/tally-test-dao-sepolia
    "eip155:11155111:0x865d09e9A99c383Bf64baFFb4C9C7d2f17a6A9B2": [
      {
        name: "Governor",
        description: "This is the Governor contract based on OZ Governor.",
        address: "0x865d09e9A99c383Bf64baFFb4C9C7d2f17a6A9B2",
      },
      {
        name: "cUSDCv3",
        description:
          "This is the main proxy contract for interacting with the first Compound III market. The address is fixed and independent from future upgrades to the market.",
        address: "0xAec1F48e02Cfb822Be958B68C7957156EB3F0b6e",
      },
      {
        name: "Comet Factory",
        description:
          "This is the factory contract capable of producing instances of the Comet implementation/logic contract, and invoked by the Configurator.",
        address: "0x91663d91795049C3D4afC85A7d0AdB15238750aa",
      },
    ],

    // /gov/compound-testnet-oz
    "eip155:11155111:0xB394D62585BC5D8e096Fbb7058768248B0a3D6fC": [
      {
        name: "Governor",
        description: "This is the Governor contract based on OZ Governor.",
        address: "0xB394D62585BC5D8e096Fbb7058768248B0a3D6fC",
      },
      {
        name: "Faucet",
        description:
          "Faucet contract to mint new tokens. If you want to mint COMP, then use 0xA6c8D1c55951e8AC44a0EaA959Be5Fd21cc07531 as token param.",
        address: "0x68793eA49297eB75DFB4610B68e076D2A5c7646C",
      },
      {
        name: "cUSDCv3",
        description:
          "This is the main proxy contract for interacting with the first Compound III market. The address is fixed and independent from future upgrades to the market.",
        address: "0xAec1F48e02Cfb822Be958B68C7957156EB3F0b6e",
      },
      {
        name: "Comet Factory",
        description:
          "This is the factory contract capable of producing instances of the Comet implementation/logic contract, and invoked by the Configurator.",
        address: "0x91663d91795049C3D4afC85A7d0AdB15238750aa",
      },
    ],
  },
}

export const getAddressBookContracts = (
  govId: string,
): AddressBookContract[] | undefined => {
  return (
    ADDRESS_BOOK?.[process.env.NEXT_PUBLIC_TALLY_API_URL]?.[govId] ?? undefined
  )
}

export const getAddressBookContract = (
  govId: string,
  targetAddress: string,
): AddressBookContract | undefined => {
  const contracts = getAddressBookContracts(govId)

  if (contracts) {
    const contract = contracts.find((contract) =>
      isEqual(contract.address.toLowerCase(), targetAddress.toLowerCase()),
    )

    return contract
  }

  return undefined
}
